import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, List, alpha, IconButton } from '@mui/material';
import { useLinks } from '../context/LinksContext';
import SectionWrapper from './SectionWrapper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

const normalizeSectorItem = (item, itemIndex) => {
    if (typeof item === 'string') {
        return {
            id: `item-${itemIndex + 1}`,
            label: item,
            description: ''
        };
    }

    if (item && typeof item === 'object') {
        return {
            ...item,
            id: item.id || `item-${itemIndex + 1}`,
            label: (item.label || '').toString(),
            description: (item.description || '').toString()
        };
    }

    return {
        id: `item-${itemIndex + 1}`,
        label: '',
        description: ''
    };
};

const normalizeSector = (sector, sectorIndex) => {
    const source = sector && typeof sector === 'object' ? sector : {};
    const rawItems = Array.isArray(source.items) ? source.items : [];

    return {
        ...source,
        id: source.id || `col${sectorIndex + 1}`,
        title: (source.title || '').toString(),
        promise: (source.promise || '').toString(),
        items: rawItems.map((item, itemIndex) => normalizeSectorItem(item, itemIndex))
    };
};

/**
 * GENESIS: MATRIX FLOW (REBOOT) - ADAPTED FOR FOOTER "WHAT WE DO"
 * Concept: Operational Grid / Data Stream (Four Rectangles)
 * Requirement: Clickable closed rectangles that open to reveal details.
 * Status: "Smart Expansion" (Dynamic Grid + Minmax + Internal Scroll)
 */
const WhatWeDoSection = () => {
    const { linksData } = useLinks();
    const [activeSector, setActiveSector] = useState(null); // 'col1', 'col2', etc.
    const [activeService, setActiveService] = useState(null); // specific service ID
    const sectionRef = useRef(null);

    // -- STYLES (Extracted from Overview DNA) --
    // Colors
    const BG_CARD = '#0A0A0A';     // Portal Dark
    const BORDER = '#333333';      // Structure Grey
    const ACCENT = '#CC0000';      // R5 Red
    const TEXT_MAIN = '#FFFFFF';

    // Typography
    const fontHeader = '"Inter", sans-serif';
    const fontCode = '"JetBrains Mono", monospace';

    const sectionTitleSx = {
        fontFamily: fontHeader,
        fontSize: 'clamp(1.05rem, 0.55vw + 0.75rem, 1.35rem)',
        letterSpacing: '-0.02em',
        fontWeight: 800,
        textTransform: 'none',
        lineHeight: 1.05,
        color: TEXT_MAIN,
        display: 'flex',
        // Cursor-like marker: align to text base (not vertical center).
        alignItems: 'flex-end',
        textRendering: 'geometricPrecision',
        WebkitFontSmoothing: 'antialiased',
    };

    const quadrantTitleSx = {
        fontFamily: fontHeader,
        fontSize: { xs: 'clamp(1.25rem, 3.0vmin, 1.7rem)', md: 'clamp(1.45rem, 1.9vmin, 1.9rem)' },
        fontWeight: 700,
        letterSpacing: '-0.02em',
        textTransform: 'none',
        lineHeight: 1.08,
        // the color is controlled by active state at render-time
        overflowWrap: 'anywhere',
        hyphens: 'auto',
        maxWidth: '100%',
        textRendering: 'geometricPrecision',
        WebkitFontSmoothing: 'antialiased',
    };

    // Handle Sector Toggle
    const handleSectorClick = (sectorId) => {
        if (activeSector === sectorId) {
            setActiveSector(null); // Close
        } else {
            setActiveSector(sectorId);
            setActiveService(null); // Reset service selection
        }
    };

    // Helper: Chunk columns for the grid (assuming linksData has columns)
    const sectors = useMemo(() => {
        const rawColumns = Array.isArray(linksData?.columns) ? linksData.columns : [];
        return rawColumns.map((sector, sectorIndex) => normalizeSector(sector, sectorIndex));
    }, [linksData]);
    const activeSectorData = useMemo(
        () => sectors.find((s) => s.id === activeSector) || null,
        [sectors, activeSector]
    );

    useEffect(() => {
        // On section entry, keep the matrix closed by default.
        // This prevents the initial expanded row from pushing/cropping the bottom quadrants on some viewports.
        const el = sectionRef.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') {
            setActiveSector(null);
            setActiveService(null);
            return;
        }

        const scroller = document.querySelector('.scroller');
        const root = scroller || null;

        const io = new IntersectionObserver(
            (entries) => {
                const entry = entries && entries[0];
                if (!entry || !entry.isIntersecting) return;
                setActiveSector(null);
                setActiveService(null);
            },
            { root, threshold: 0.6 }
        );

        io.observe(el);
        return () => io.disconnect();
        // Intentionally not depending on activeSector: "entry resets state" behavior should be stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // RENDER: MATRIX NODE (SECTOR) - "Index" view only (no internal scroll, no details here)
    const MatrixNode = ({ sector, index }) => {
        // Pad index to "01", "02"
        const number = (index + 1).toString().padStart(2, '0');
        const title = sector.title || "SYSTEM_MODULE"; // Fallback for empties
        const isActive = activeSector === sector.id;

        return (
            <Box
                id={`sector-${sector.id}`}
                role="button"
                tabIndex={0}
                aria-label={`Abrir detalhamento da área ${number}: ${title}`}
                onClick={() => handleSectorClick(sector.id)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSectorClick(sector.id);
                    }
                }}
                sx={{
                    height: '100%', // Fill the grid cell
                    width: '100%',
                    scrollMarginTop: 'calc(var(--header-h, 60px) + 20px)', // Keep scroll margin
                    border: `1px solid ${isActive ? ACCENT : BORDER}`,
                    bgcolor: BG_CARD, // Keep background solid/clean
                    boxShadow: isActive ? `0 0 30px ${alpha(ACCENT, 0.15)}` : 'none', // Glow instead of fill
                    transition: 'all 0.36s cubic-bezier(0.2, 0.8, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden', // Index tile should always be clipped
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    '&:hover': {
                        borderColor: isActive ? ACCENT : alpha(TEXT_MAIN, 0.5),
                        boxShadow: `0 16px 30px ${alpha('#000', 0.28)}`,
                        transform: 'translateY(-2px)',
                        '& .sector-label': {
                            color: ACCENT
                        },
                        '& .node-number': {
                            transform: 'scale(1.03)',
                            opacity: 0.92
                        },
                        '& .node-cta': {
                            color: '#FFF',
                            opacity: 1,
                            transform: 'translateY(0)'
                        },
                        '& .node-cta-text': {
                            color: '#FFF',
                            opacity: 1,
                            textShadow: '0 0 10px rgba(255, 255, 255, 0.35)'
                        },
                        '& .node-cta-icon': {
                            transform: 'translateX(3px) translateY(-1px)'
                        }
                    },
                    '&:focus-visible': {
                        outline: `1px solid ${ACCENT}`,
                        outlineOffset: '-1px'
                    }
                }}
            >
                {/* --- 1. DECORATIVE BACKGROUND LAYER (CLIPPED) --- */}
                <Box sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    overflow: 'hidden', // Clips the giant number
                    pointerEvents: 'none',
                    borderRadius: '14px',
                    zIndex: 0
                }}>
	                    <Typography className="node-number" sx={{
                        position: 'absolute',
                        // Bottom-right watermark so it never fights the title.
                        bottom: 'clamp(10px, 2.4vmin, 28px)',
                        right: 'clamp(12px, 2.6vmin, 34px)',
		                        transform: 'scale(1)',
		                        fontSize: { xs: 'clamp(7.5rem, 16vmin, 10rem)', md: 'clamp(9rem, 13vmin, 12.5rem)' },
		                        fontWeight: 900,
		                        fontFamily: fontCode,
		                        // Original filled number, but always red (no hover dependency).
		                        color: ACCENT,
		                        opacity: 1,
		                        lineHeight: 1,
		                        transition: 'transform 0.28s ease, color 0.3s ease, opacity 0.3s ease'
		                    }}>
		                        {number}
                    </Typography>
                </Box>

                {/* --- 2. CONTENT LAYER (ACTUAL UI) --- */}
                <Box sx={{ position: 'relative', zIndex: 2, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>

                    {/* HEADLINE (Title) */}
                    <Box sx={{
                        // Closed nodes have less vertical budget (especially bottom row on some viewports).
                        // Keep titles readable via tighter padding when closed.
                        p: { xs: 2.5, md: isActive ? 5 : 4 },
                        borderBottom: isActive ? `1px solid ${ACCENT}` : 'none',
                        flexShrink: 0, // Header never shrinks
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                    }}>
                        <Box>
                            <Typography sx={{
                                fontFamily: fontCode,
                                fontSize: '0.75rem',
                                color: isActive ? ACCENT : '#666',
                                mb: 1,
                                letterSpacing: '0.1em'
                            }}>
                                {`// AREA_0${index + 1}`}
                            </Typography>
                            <Typography
                                className="sector-label"
                                variant="h3"
                                sx={{
                                    ...quadrantTitleSx,
	                                    color: isActive ? '#FFF' : '#DDD',
	                                    // Prevent "disappearing" text: clamp instead of being clipped by the grid cell.
	                                    display: '-webkit-box',
	                                    WebkitLineClamp: 4,
	                                    WebkitBoxOrient: 'vertical',
	                                    overflow: 'hidden',
	                                    textOverflow: 'ellipsis',
	                                }}
	                            >
		                                {title}
		                            </Typography>

                        </Box>
                        {/* Minimal Indicator */}
                        <Box sx={{
                            width: '12px',
                            height: '12px',
                            bgcolor: isActive ? ACCENT : '#333',
                            transition: 'background-color 0.3s'
                        }} />
                    </Box>

                    <Box
                        className="node-cta"
                        sx={{
                            mt: 'auto',
                            px: { xs: 2.5, md: isActive ? 5 : 4 },
                            pb: { xs: 2.35, md: 2.85 },
                            pt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: alpha(TEXT_MAIN, 0.62),
                            opacity: 0,
                            transform: 'translateY(6px)',
                            borderTop: `1px solid ${alpha(TEXT_MAIN, 0.08)}`,
                            transition: 'color 0.28s ease, opacity 0.34s ease, transform 0.34s ease'
                        }}
                    >
                        <Typography
                            className="node-cta-text"
                            sx={{
                                fontFamily: fontCode,
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: alpha(TEXT_MAIN, 0.92),
                                opacity: 0.9,
                                transition: 'color 0.28s ease, opacity 0.28s ease, text-shadow 0.28s ease'
                            }}
                        >
                            Clique para ver detalhamento
                        </Typography>
                        <ArrowOutwardIcon
                            className="node-cta-icon"
                            sx={{
                                fontSize: 18,
                                transition: 'transform 0.24s ease'
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

    const FocusPanel = ({ sector }) => {
        if (!sector) return null;
        const sectorIndex = sectors.findIndex((s) => s.id === sector.id);
        const number = (sectorIndex + 1).toString().padStart(2, '0');

				        return (
					            <Box sx={{
					                // Split de leitura: painel editorial (sem efeitos) para leitura limpa.
					                border: `1px solid ${BORDER}`,
					                bgcolor: '#050505',
					                color: TEXT_MAIN,
				                position: 'relative',
				                overflow: 'hidden',
				                display: 'flex',
				                flexDirection: 'column',
				                minHeight: 0,
				                width: '100%',
					            }}>
		                <Box sx={{
		                    position: 'relative',
		                    zIndex: 1,
		                    display: 'grid',
		                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(0, 1.08fr)' },
	                    gap: { xs: 0, md: 0 },
	                    minHeight: 0,
	                }}>
	                    {/* Left: Title block */}
		                    <Box
                                role="button"
                                tabIndex={0}
                                aria-label={`Voltar para quadrantes da área ${number}`}
                                onClick={() => handleSectorClick(sector.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSectorClick(sector.id);
                                    }
                                }}
                                sx={{
		                        p: 'clamp(18px, 3.2vmin, 44px)',
		                        borderBottom: { xs: `1px solid ${BORDER}`, md: 'none' },
		                        borderRight: { md: `1px solid ${BORDER}` },
		                        position: 'relative',
		                        overflow: 'hidden',
		                        display: 'flex',
		                        flexDirection: 'column',
		                        justifyContent: 'space-between',
		                        minHeight: 0,
                                    cursor: 'pointer',
		                    }}>
	                        {/* Watermark: numero da area (apagado, fica vermelho no hover) */}
				                        <Typography
					                            className="focus-number"
					                            sx={{
					                                position: 'absolute',
					                                right: 'clamp(6px, 1.2vmin, 14px)',
					                                top: { xs: '70%', md: '66%' },
					                                transform: 'translateY(-50%)',
					                                fontFamily: fontCode,
					                                fontWeight: 900,
				                                fontSize: { xs: '9rem', md: 'clamp(11rem, 19vmin, 16rem)' },
				                                lineHeight: 0.8,
				                                letterSpacing: '-0.06em',
				                                color: alpha(ACCENT, 0.70),
				                                opacity: 0.44,
				                                pointerEvents: 'none',
				                                zIndex: 0,
				                                // Fade-out near bottom so it doesn't "brutal-overlap" the footer hint text.
				                                // Keep almost all of the number visible; only soften the very bottom.
				                                WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 84%, rgba(0,0,0,0) 100%)',
				                                maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 84%, rgba(0,0,0,0) 100%)',
				                            }}
				                        >
				                            {number}
				                        </Typography>

	                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
	                            <Box sx={{ minWidth: 0, position: 'relative', zIndex: 1 }}>
	                                <Typography sx={{
	                                    fontFamily: fontCode,
	                                    fontSize: '0.78rem',
	                                    color: alpha(TEXT_MAIN, 0.55),
                                    mb: 1.5,
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                            }}>
                                    {`// AREA_DE_ATUACAO / AREA_${number}`}
                                </Typography>

                                <Typography variant="h2" sx={{
                                    fontFamily: fontHeader,
                                    fontSize: { xs: 'clamp(2.1rem, 7vmin, 3rem)', md: 'clamp(2.4rem, 3.8vmin, 3.4rem)' },
                                    fontWeight: 600,
                                    color: TEXT_MAIN,
                                    textTransform: 'uppercase',
                                    lineHeight: 1.02,
                                    letterSpacing: '-0.03em',
                                    overflowWrap: 'anywhere',
                                    hyphens: 'auto',
                                }}>
                                    {sector.title}<Box component="span" sx={{ color: ACCENT }}>.</Box>
                                </Typography>
                            </Box>

                            <IconButton
                                aria-label="Voltar"
                                onClick={() => handleSectorClick(sector.id)}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '14px',
                                    color: TEXT_MAIN,
                                    border: `1px solid ${BORDER}`,
                                    bgcolor: 'transparent',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: alpha(TEXT_MAIN, 0.04), borderColor: alpha(TEXT_MAIN, 0.35) },
                                }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha(TEXT_MAIN, 0.10)}` }}>
                            <Typography sx={{
                                fontFamily: fontHeader,
                                color: alpha(TEXT_MAIN, 0.78),
                                fontSize: 'clamp(0.98rem, 1.7vmin, 1.08rem)',
                                lineHeight: 1.6,
                                maxWidth: '52ch',
                            }}>
                                {number} / Área. Selecione um item para ver em detalhe como a R5 atua neste segmento.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: Services */}
                    <Box sx={{ p: 'clamp(18px, 3.2vmin, 44px)', pt: { xs: 'clamp(18px, 3.2vmin, 44px)', md: 'clamp(18px, 3.2vmin, 44px)' } }}>
                        <Typography sx={{
                            fontFamily: fontCode,
                            fontSize: '0.78rem',
                            color: alpha(TEXT_MAIN, 0.55),
                            mb: 2,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                        }}>
                            {'// ITENS_DA_AREA'}
                        </Typography>

                        <List disablePadding sx={{ borderTop: `1px solid ${BORDER}` }}>
                            {sector.items.map((item, itemIndex) => {
                                const itemId = item.id || `${sector.id}-item-${itemIndex + 1}`;
                                const isServiceActive = activeService === itemId;
                                return (
                                    <Box key={itemId} sx={{ mb: 0 }}>
		                                        <Box
		                                            role="button"
		                                            tabIndex={0}
		                                            onClick={(e) => {
		                                                e.stopPropagation();
		                                                setActiveService(isServiceActive ? null : itemId);
		                                            }}
	                                            sx={{
	                                                py: 2,
	                                                px: { xs: 2, md: 2.5 },
	                                                cursor: 'pointer',
	                                                display: 'flex',
	                                                alignItems: 'center',
	                                                justifyContent: 'space-between',
	                                                gap: 3,
	                                                borderBottom: `1px solid ${alpha(TEXT_MAIN, 0.10)}`,
	                                                bgcolor: isServiceActive ? alpha(ACCENT, 0.06) : 'transparent',
	                                                '&:hover': {
	                                                    bgcolor: alpha(TEXT_MAIN, 0.03),
	                                                    '& .item-label': { color: alpha(TEXT_MAIN, 0.9) },
	                                                    '& .item-indicator': { color: alpha(TEXT_MAIN, 0.75) }
	                                                }
	                                            }}
	                                        >
                                            <Typography className="item-label" sx={{
                                                fontFamily: fontCode,
                                                fontWeight: 400,
                                                fontSize: 'clamp(0.82rem, 1.1vmin, 0.95rem)',
                                                color: isServiceActive ? alpha(TEXT_MAIN, 0.92) : alpha(TEXT_MAIN, 0.62),
                                                letterSpacing: '0.12em',
                                                textTransform: 'uppercase',
                                                overflowWrap: 'anywhere',
                                                minWidth: 0,
                                                pr: 2,
                                                lineHeight: 1.35,
                                            }}>
                                                {item.label || 'ITEM'}
                                            </Typography>

                                            <Box sx={{ width: 72, textAlign: 'right', flexShrink: 0 }}>
                                                <Typography className="item-indicator" sx={{
                                                    fontFamily: fontCode,
                                                    fontSize: '0.78rem',
                                                    fontWeight: 400,
                                                    color: isServiceActive ? alpha(ACCENT, 0.9) : alpha(TEXT_MAIN, 0.32),
                                                    letterSpacing: '0.08em',
                                                }}>
                                                    {isServiceActive ? '[-]' : '[+]'}
                                                </Typography>
                                            </Box>
	                                        </Box>

	                                        {isServiceActive && (
	                                            <Box sx={{
	                                                borderLeft: `1px solid ${ACCENT}`,
	                                                bgcolor: '#0A0A0A',
	                                                p: { xs: 2.5, md: 3 },
	                                                pl: { xs: 4, md: 4.5 },
	                                                borderBottom: `1px solid ${alpha(TEXT_MAIN, 0.10)}`,
	                                            }}>
                                                <Typography sx={{
                                                    fontFamily: fontCode,
                                                    fontSize: '0.72rem',
                                                    color: alpha(TEXT_MAIN, 0.55),
                                                    mb: 1.2,
                                                    letterSpacing: '0.18em',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {'// DETALHE'}
                                                </Typography>
                                                <Typography sx={{
                                                    fontFamily: fontHeader,
                                                    color: alpha(TEXT_MAIN, 0.92),
                                                    lineHeight: 1.75,
                                                    fontSize: 'clamp(1.0rem, 1.7vmin, 1.15rem)',
                                                    fontWeight: 400,
                                                    overflowWrap: 'anywhere',
                                                    hyphens: 'auto',
                                                    maxWidth: '72ch',
                                                }}>
                                                    {(item.description || "DADOS SIGILOSOS RESTRITOS.")}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </List>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <SectionWrapper
            ref={sectionRef}
            id="o-que-fazemos"
            className="what-we-do-section"
            noPadding
            sx={{ bgcolor: '#050505', color: TEXT_MAIN }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', pr: { xs: 0, md: 6 }, bgcolor: '#050505' }}>

                {/* STANDARD SECTION HEADER (Matched to NewsGrid) */}
                <Box sx={{
                    px: { xs: 3, md: 5 },
                    py: 3,
                    borderBottom: `1px solid ${BORDER}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    bgcolor: BG_CARD // Match Card BG for seamless look
                }}>
	                    <Typography variant="h2" sx={sectionTitleSx}>
	                        Áreas de Atuação
	                        <Box component="span" sx={{
	                            display: 'inline-block',
	                            width: 10,
	                            height: 10,
	                            borderRadius: '2px',
	                            bgcolor: ACCENT,
	                            ml: 1.5,
	                            flex: '0 0 auto',
	                            mb: '0.12em',
	                            boxShadow: `0 0 18px ${alpha(ACCENT, 0.25)}`,
	                        }} />
	                    </Typography>
                </Box>

                {/* MAIN CONTENT */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', position: 'relative' }}>

                    {/* Left decoration removed: caused visual "teeth" + no utility on this section */}

                    {activeSectorData ? (
                        <Box sx={{ flex: 1, minHeight: 0, height: '100%', p: { xs: 2, md: 2.5 } }}>
                            <FocusPanel sector={activeSectorData} />
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1, md: 1.5 } }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                                gridTemplateRows: { xs: 'auto', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                                minHeight: 0,
                                height: '100%',
                                gap: '1px',
                                bgcolor: BORDER,
                                border: `1px solid ${BORDER}`,
                                transition: 'grid-template-rows 0.5s cubic-bezier(0.2, 0.8, 0.2, 1), grid-template-columns 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)'
                            }}>
                                {sectors.map((sector, index) => (
                                    <MatrixNode
                                        key={sector.id}
                                        sector={sector}
                                        index={index}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </SectionWrapper>
    );
};

export default WhatWeDoSection;
